import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AutoStatusToast from '../components/common/AutoStatusToast';
import AdminLearningContentTable from '../components/admin/AdminLearningContentTable';
import { useAutoStatusToast } from '../hooks/useAutoStatusToast';
import { GradeTrackLeaveContext } from '../contexts/GradeTrackLeaveContext';
import { useProgramSidebar } from '../contexts/ProgramSidebarContext';
import { useAdminLearningContentRows } from '../hooks/useAdminLearningContentRows';
import { useWorkspaceLearningContentRows } from '../hooks/useWorkspaceLearningContentRows';
import { AdminLearningContentSubCategory, AdminLearningContentRowUpsert } from '../types/domain';
import { getAdminLearningContentTemplates } from '../services/adminLearningContentService';
import {
  createWorkspaceLearningContentTemplate,
  deleteWorkspaceLearningContentTemplate,
  getWorkspaceLearningContentTemplates,
  reorderWorkspaceLearningContentTemplates,
  renameWorkspaceLearningContentTemplate,
} from '../services/workspaceLearningContentService';
import ProgramToolbarUndoRedoButtons from '../components/program/ProgramToolbarUndoRedoButtons';
import { mergeAdminLearningRowsByPublisherFilter } from '../utils/adminLearningContentPublisherMerge';
import { adminLearningContentRowHasCellData } from '../utils/adminLearningContentRowHasData';
import { loadAdminLearningContentTemplatesCache } from '../utils/adminLearningContentCache';
import { isAdminLearningContentTemplatesSessionDbSynced } from '../utils/adminLearningContentSessionSync';
import { lowestGradeNumberFromDisplayName } from '../utils/gradeDisplayName';
import { loadTeamLeaderWorkspaceIdForLearningShareFromCache } from '../utils/myTeamMembershipCache';
import { loadWorkspaceLearningContentTemplatesCache } from '../utils/workspaceLearningContentCache';
import { getTeamLeaderWorkspaceIdForLearningShare } from '../services/teamService';
import { getWorkspaceShareGradeId } from '../utils/workspaceShareGradeId';
import {
  EMPTY_LEARNING_CONTENT_TEMPLATE_SELECTION,
  LearningContentTemplateSelection,
  resolveLearningContentTemplateSelection,
} from '../utils/learningContentTemplateSelection';

const GRADE_TABS = [1, 2, 3, 4, 5, 6] as const;
const SUB_CATEGORIES: Array<{ key: AdminLearningContentSubCategory; label: (grade: number) => string }> = [
  { key: 'term1', label: (grade) => `${grade}학년 1학기` },
  { key: 'term2', label: (grade) => `${grade}학년 2학기` },
  { key: 'common', label: (grade) => `${grade}학년 학기 구분 없음` },
];

type AddTemplateScope = { common: boolean; term1: boolean; term2: boolean };

function defaultAddTemplateScopeForSubCategory(
  sc: AdminLearningContentSubCategory
): AddTemplateScope {
  if (sc === 'common') return { common: true, term1: false, term2: false };
  if (sc === 'term1') return { common: false, term1: true, term2: false };
  return { common: false, term1: false, term2: true };
}

function addTemplateScopeToTargets(s: AddTemplateScope): AdminLearningContentSubCategory[] {
  const out: AdminLearningContentSubCategory[] = [];
  if (s.term1) out.push('term1');
  if (s.term2) out.push('term2');
  if (s.common) out.push('common');
  return out;
}

function pickPrimarySubCategory(
  targets: AdminLearningContentSubCategory[]
): AdminLearningContentSubCategory {
  const order: AdminLearningContentSubCategory[] = ['term1', 'term2', 'common'];
  for (const k of order) {
    if (targets.includes(k)) return k;
  }
  return targets[0];
}

type TemplateScopeBootstrap = {
  adminNames: string[];
  personalNames: string[];
  teamNames: string[];
  teamWs: string | null;
  selection: LearningContentTemplateSelection;
};

function emptyTemplateScopeBootstrap(): TemplateScopeBootstrap {
  return {
    adminNames: [],
    personalNames: [],
    teamNames: [],
    teamWs: null,
    selection: EMPTY_LEARNING_CONTENT_TEMPLATE_SELECTION,
  };
}

function bootstrapTemplateScopeFromCache(
  workspaceId: string,
  gradeId: string,
  gradeNumber: (typeof GRADE_TABS)[number],
  subCategory: AdminLearningContentSubCategory,
  prevSelection: LearningContentTemplateSelection = EMPTY_LEARNING_CONTENT_TEMPLATE_SELECTION,
  teamShareGradeId: string | null = null
): TemplateScopeBootstrap {
  if (!workspaceId || !gradeId) return emptyTemplateScopeBootstrap();

  const adminCached = isAdminLearningContentTemplatesSessionDbSynced(gradeNumber, subCategory)
    ? loadAdminLearningContentTemplatesCache(workspaceId, gradeNumber, subCategory)
    : { found: false as const, templates: [] };
  const wsCached = loadWorkspaceLearningContentTemplatesCache(workspaceId, gradeId, gradeNumber, subCategory);
  const teamWsCached = loadTeamLeaderWorkspaceIdForLearningShareFromCache(workspaceId);
  const teamWs = teamWsCached === undefined ? null : teamWsCached;

  const adminNames = adminCached.found
    ? adminCached.templates.map((row) => (row.template_name ?? '').trim()).filter(Boolean)
    : [];
  const personalNames = wsCached.found
    ? wsCached.templates.map((row) => (row.template_name ?? '').trim()).filter(Boolean)
    : [];
  let teamNames: string[] = [];
  if (teamWs && teamShareGradeId) {
    const teamCached = loadWorkspaceLearningContentTemplatesCache(teamWs, teamShareGradeId, gradeNumber, subCategory);
    if (teamCached.found) {
      teamNames = teamCached.templates.map((row) => (row.template_name ?? '').trim()).filter(Boolean);
    }
  }

  if (!adminNames.length && !personalNames.length && !teamNames.length) {
    return { adminNames, personalNames, teamNames, teamWs, selection: EMPTY_LEARNING_CONTENT_TEMPLATE_SELECTION };
  }

  return {
    adminNames,
    personalNames,
    teamNames,
    teamWs,
    selection: resolveLearningContentTemplateSelection(
      prevSelection,
      adminNames,
      personalNames,
      teamNames
    ),
  };
}

const SortableTemplateItem: React.FC<{
  id: string;
  active: boolean;
  busy: boolean;
  onSelect: () => void;
  onRename: () => void;
  onDelete: () => void;
}> = ({ id, active, busy, onSelect, onRename, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.84 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`cp-admin-learning-template-item${active ? ' is-active' : ''}${isDragging ? ' is-dragging' : ''}`}
      aria-selected={active}
      aria-label={`${id}, 사용자 편제`}
      title="이 워크스페이스에서 추가한 사용자 편제입니다."
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      {...attributes}
      {...listeners}
    >
      <div className="cp-admin-learning-template-tab cp-admin-learning-template-tab--with-dot">
        <span className="cp-admin-learning-template-dot cp-admin-learning-template-dot--user" aria-hidden />
        <span className="cp-admin-learning-template-tab-label">{id}</span>
      </div>
      <div className="cp-admin-learning-template-item-actions">
        <button
          type="button"
          className="track-icon-button cp-admin-learning-template-icon-btn"
          title="편제 이름 수정"
          onClick={(e) => {
            e.stopPropagation();
            onRename();
          }}
          disabled={busy}
          aria-label="편제 수정"
        >
          <img src="/edit.svg" alt="편제 수정" className="track-icon" />
        </button>
        <button
          type="button"
          className="track-icon-button cp-admin-learning-template-icon-btn cp-admin-learning-template-icon-btn-delete"
          title="편제 삭제"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          disabled={busy}
          aria-label="편제 삭제"
        >
          <img src="/Trash.svg" alt="편제 삭제" className="track-icon" />
        </button>
      </div>
    </div>
  );
};

const ReadonlyAdminTemplateTabItem: React.FC<{
  name: string;
  active: boolean;
  busy: boolean;
  onSelect: () => void;
}> = ({ name, active, busy, onSelect }) => (
  <div
    role="tab"
    className={`cp-admin-learning-template-item cp-admin-learning-template-item--readonly${active ? ' is-active' : ''}`}
    aria-selected={active}
    aria-label={`${name}, 관리자 기본 편제`}
    title="관리자 메뉴「학습내용 관리」에서 등록된 기본 편제(읽기 전용)"
    onClick={() => {
      if (!busy) onSelect();
    }}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!busy) onSelect();
      }
    }}
    tabIndex={0}
  >
    <div className="cp-admin-learning-template-tab cp-admin-learning-template-tab--with-dot">
      <span className="cp-admin-learning-template-dot cp-admin-learning-template-dot--default" aria-hidden />
      <span className="cp-admin-learning-template-tab-label">{name}</span>
    </div>
  </div>
);

const ReadonlyTeamLeaderTemplateTabItem: React.FC<{
  label: string;
  active: boolean;
  busy: boolean;
  onSelect: () => void;
}> = ({ label, active, busy, onSelect }) => (
  <div
    role="tab"
    className={`cp-admin-learning-template-item cp-admin-learning-template-item--readonly${active ? ' is-active' : ''}`}
    aria-selected={active}
    aria-label={`${label}, 팀장 사용자 편제`}
    title="소속 팀 팀장 워크스페이스에서 추가한 편제(읽기 전용)"
    onClick={() => {
      if (!busy) onSelect();
    }}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!busy) onSelect();
      }
    }}
    tabIndex={0}
  >
    <div className="cp-admin-learning-template-tab cp-admin-learning-template-tab--with-dot">
      <span className="cp-admin-learning-template-dot cp-admin-learning-template-dot--team" aria-hidden />
      <span className="cp-admin-learning-template-tab-label">{label}</span>
    </div>
  </div>
);

const WorkspaceLearningContentPage: React.FC<{ currentGradeId?: string; isActive?: boolean }> = ({
  currentGradeId = '',
  isActive = true,
}) => {
  const { workspaceId, programFocusGradeId, grades } = useProgramSidebar();
  const effectiveFocusGradeId = (programFocusGradeId || currentGradeId || '').trim();
  const focusGradeRow = useMemo(
    () => (effectiveFocusGradeId ? grades.find((g) => g.id === effectiveFocusGradeId) ?? null : null),
    [effectiveFocusGradeId, grades]
  );
  const classDefaultGradeNumber = useMemo((): (typeof GRADE_TABS)[number] => {
    if (!focusGradeRow) return 1;
    return lowestGradeNumberFromDisplayName(focusGradeRow.name);
  }, [focusGradeRow]);
  const [gradeOverride, setGradeOverride] = useState<(typeof GRADE_TABS)[number] | null>(null);
  const gradeNumber = gradeOverride ?? classDefaultGradeNumber;
  const [subCategory, setSubCategory] = useState<AdminLearningContentSubCategory>('term1');
  const [adminTemplateNames, setAdminTemplateNames] = useState<string[]>([]);
  const [personalTemplates, setPersonalTemplates] = useState<string[]>([]);
  const [templateSelection, setTemplateSelection] = useState<LearningContentTemplateSelection>(
    EMPTY_LEARNING_CONTENT_TEMPLATE_SELECTION
  );
  const templateName = templateSelection.name;
  const templateScope = templateSelection.scope;
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [addTemplateScope, setAddTemplateScope] = useState<AddTemplateScope>(() =>
    defaultAddTemplateScopeForSubCategory('term1')
  );
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameSourceName, setRenameSourceName] = useState('');
  const [renameDraftName, setRenameDraftName] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetName, setDeleteTargetName] = useState('');
  const [templateBusy, setTemplateBusy] = useState(false);
  const createTemplateConfirmRef = useRef<HTMLButtonElement>(null);
  const templateSelectionRef = useRef(templateSelection);
  templateSelectionRef.current = templateSelection;
  const { toastState: saveToastState, showSuccess: showSaveSuccess, showError: showSaveError } = useAutoStatusToast(3000);
  const [tableActions, setTableActions] = useState<{
    undo: () => void;
    redo: () => void;
    save: () => Promise<void>;
  } | null>(null);
  const [tableActionState, setTableActionState] = useState({
    canUndo: false,
    canRedo: false,
    canSave: false,
  });
  const { setWorkspaceLearningContentDirty, saveWorkspaceLearningContentRef } = React.useContext(GradeTrackLeaveContext);
  const [templateOrderDirty, setTemplateOrderDirty] = useState(false);
  /** key=currentName, value=originalName(마지막 DB 반영 기준) */
  const [templateOriginalNameByCurrent, setTemplateOriginalNameByCurrent] = useState<Record<string, string>>({});
  /** 출판사 탭이 있을 때 선택된 출판사(trim). 탭이 없으면 '' */
  const [publisherFilter, setPublisherFilter] = useState<string>('');
  /** 팀원일 때 팀장 워크스페이스 id (본인이 팀장이면 null) */
  const [teamLeaderLearningWorkspaceId, setTeamLeaderLearningWorkspaceId] = useState<string | null>(null);
  const [teamShareGradeId, setTeamShareGradeId] = useState<string | null>(null);
  const [teamTemplateNames, setTeamTemplateNames] = useState<string[]>([]);
  const isAdminTemplate = templateScope === 'admin';
  const teamLeaderViewActive = templateScope === 'team';
  const effectiveUserLearningWorkspaceId =
    !isAdminTemplate && teamLeaderViewActive && teamLeaderLearningWorkspaceId
      ? teamLeaderLearningWorkspaceId
      : workspaceId;
  const adminRows = useAdminLearningContentRows({
    workspaceId,
    gradeNumber,
    subCategory,
    templateName: isAdminTemplate ? templateName : '',
  });
  const rowsGradeId =
    !isAdminTemplate && teamLeaderViewActive && teamShareGradeId
      ? teamShareGradeId
      : effectiveFocusGradeId;
  const workspaceRows = useWorkspaceLearningContentRows({
    workspaceId: effectiveUserLearningWorkspaceId,
    gradeId: rowsGradeId,
    gradeNumber,
    subCategory,
    templateName: !isAdminTemplate && templateName ? templateName : '',
  });
  const rows = isAdminTemplate ? adminRows.rows : workspaceRows.rows;
  const loading = isAdminTemplate ? adminRows.loading : workspaceRows.loading;
  const saving = isAdminTemplate ? adminRows.saving : workspaceRows.saving;
  const error = (isAdminTemplate ? adminRows.error : workspaceRows.error) ?? null;
  const { saveRows } = workspaceRows;

  const publisherOptions = useMemo(() => {
    const map = new Map<string, string>();
    let hasNoPublisherRowsWithData = false;
    for (const r of rows) {
      const t = (r.publisher ?? '').trim();
      if (!t) {
        if (adminLearningContentRowHasCellData(r)) hasNoPublisherRowsWithData = true;
        continue;
      }
      map.set(t, t);
    }
    if (hasNoPublisherRowsWithData) map.set('', '구분없음');
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1], 'ko'));
  }, [rows]);

  /** 출판사가 입력된 행이 하나라도 있을 때만, 편제 아래에 출판사 탭 표시 */
  const showPublisherSubtabs = !!templateName.trim() && publisherOptions.length > 0;

  const rowsForTable = useMemo(() => {
    if (!showPublisherSubtabs) return rows;
    if (!publisherOptions.some(([key]) => key === publisherFilter)) {
      return rows.filter((r) => (r.publisher ?? '').trim() === publisherOptions[0]?.[0]);
    }
    return rows.filter((r) => (r.publisher ?? '').trim() === publisherFilter);
  }, [publisherFilter, publisherOptions, rows, showPublisherSubtabs]);

  const publisherToolbarSuffix = useMemo(() => {
    if (!showPublisherSubtabs) return '';
    const label = publisherOptions.find(([key]) => key === publisherFilter)?.[1] ?? publisherFilter;
    if (!label) return '';
    return ` · ${label}`;
  }, [publisherFilter, publisherOptions, showPublisherSubtabs]);

  const userLearningToolbarSuffix = useMemo(() => {
    if (isAdminTemplate) return ' (서비스 기본·읽기 전용)';
    if (teamLeaderViewActive) return ' (팀장 편제·읽기 전용)';
    return '';
  }, [isAdminTemplate, teamLeaderViewActive]);

  const newTemplateNameTaken = useMemo(() => {
    const n = newTemplateName.trim();
    if (!n) return false;
    return personalTemplates.includes(n);
  }, [personalTemplates, newTemplateName]);

  const renameTargetConflicts = useMemo(() => {
    const n = renameDraftName.trim();
    const from = renameSourceName.trim();
    if (!n) return false;
    return personalTemplates.some((p) => p !== from && p === n);
  }, [personalTemplates, renameDraftName, renameSourceName]);

  const teamReadonlyTabItems = useMemo(() => {
    if (!teamLeaderLearningWorkspaceId) return [];
    return teamTemplateNames.map((name) => ({
      key: `team:${name}`,
      templateName: name,
    }));
  }, [teamLeaderLearningWorkspaceId, teamTemplateNames]);

  const subCategoryLabel = useMemo(
    () => SUB_CATEGORIES.find((v) => v.key === subCategory)?.label(gradeNumber) ?? '',
    [gradeNumber, subCategory]
  );
  const templateDndSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  const handleSave = React.useCallback(
    async (nextRows: AdminLearningContentRowUpsert[]) => {
      if (isAdminTemplate || teamLeaderViewActive) return;
      if (!showPublisherSubtabs) {
        await saveRows(nextRows);
      } else {
        const merged = mergeAdminLearningRowsByPublisherFilter(rows, nextRows, publisherFilter);
        await saveRows(merged);
      }
      showSaveSuccess(`${subCategoryLabel} 데이터가 저장되었습니다.`);
    },
    [isAdminTemplate, teamLeaderViewActive, showPublisherSubtabs, publisherFilter, rows, saveRows, showSaveSuccess, subCategoryLabel]
  );

  const requestPublisherFilterChange = React.useCallback(
    (next: string) => {
      if (next === publisherFilter) return;
      if (tableActionState.canSave) {
        const ok = window.confirm(
          '저장되지 않은 변경이 있습니다. 출판사 보기를 바꾸면 표가 다시 불러와져 편집 내용이 사라질 수 있습니다. 계속할까요?'
        );
        if (!ok) return;
      }
      setPublisherFilter(next);
    },
    [publisherFilter, tableActionState.canSave]
  );

  useLayoutEffect(() => {
    setPublisherFilter('');
  }, [subCategory, gradeNumber, templateName, templateScope]);

  React.useEffect(() => {
    if (!templateName.trim()) return;
    setPublisherFilter((prev) => {
      if (publisherOptions.length === 0) return '';
      if (publisherOptions.some(([key]) => key === prev)) return prev;
      return publisherOptions[0][0];
    });
  }, [publisherOptions, templateName, subCategory, gradeNumber, templateScope]);

  const applyTemplateBootstrap = React.useCallback((boot: TemplateScopeBootstrap) => {
    setAdminTemplateNames(boot.adminNames);
    setPersonalTemplates(boot.personalNames);
    setTeamLeaderLearningWorkspaceId(boot.teamWs);
    setTeamTemplateNames(boot.teamNames);
    setTemplateSelection(boot.selection);
    setTemplateOrderDirty(false);
    setTemplateOriginalNameByCurrent(Object.fromEntries(boot.personalNames.map((name) => [name, name])));
  }, []);

  useLayoutEffect(() => {
    if (!isActive) return;
    setGradeOverride(null);
  }, [effectiveFocusGradeId, isActive]);

  useLayoutEffect(() => {
    if (!isActive || !workspaceId) return;
    if (!effectiveFocusGradeId) return;
    const boot = bootstrapTemplateScopeFromCache(
      workspaceId,
      effectiveFocusGradeId,
      gradeNumber,
      subCategory,
      templateSelectionRef.current,
      teamShareGradeId
    );
    if (!boot.adminNames.length && !boot.personalNames.length && !boot.teamNames.length) return;
    applyTemplateBootstrap(boot);
  }, [applyTemplateBootstrap, effectiveFocusGradeId, gradeNumber, isActive, subCategory, teamShareGradeId, workspaceId]);

  React.useEffect(() => {
    if (!workspaceId || !effectiveFocusGradeId) {
      setAdminTemplateNames([]);
      setPersonalTemplates([]);
      setTeamLeaderLearningWorkspaceId(null);
      setTeamShareGradeId(null);
      setTeamTemplateNames([]);
      setTemplateSelection(EMPTY_LEARNING_CONTENT_TEMPLATE_SELECTION);
      return;
    }
    let cancelled = false;

    void (async () => {
      const teamWsCached = loadTeamLeaderWorkspaceIdForLearningShareFromCache(workspaceId);
      const teamWsResolved =
        teamWsCached === undefined
          ? await getTeamLeaderWorkspaceIdForLearningShare(workspaceId).catch(() => null)
          : teamWsCached;
      if (cancelled) return;
      const teamShareResolved = teamWsResolved
        ? await getWorkspaceShareGradeId(teamWsResolved).catch(() => null)
        : null;
      if (cancelled) return;
      setTeamShareGradeId(teamShareResolved);
      const [adminList, wsList, teamList] = await Promise.all([
        getAdminLearningContentTemplates(workspaceId, gradeNumber, subCategory).catch(() => []),
        getWorkspaceLearningContentTemplates(workspaceId, effectiveFocusGradeId, gradeNumber, subCategory).catch(() => []),
        teamWsResolved && teamShareResolved
          ? getWorkspaceLearningContentTemplates(teamWsResolved, teamShareResolved, gradeNumber, subCategory).catch(() => [])
          : Promise.resolve([]),
      ]);
      if (cancelled) return;
      const adminNames = (adminList ?? [])
        .map((row) => (row.template_name ?? '').trim())
        .filter(Boolean);
      const personalNames = (wsList ?? [])
        .map((row) => (row.template_name ?? '').trim())
        .filter(Boolean);
      const teamNames = (teamList ?? [])
        .map((row) => (row.template_name ?? '').trim())
        .filter(Boolean);
      applyTemplateBootstrap({
        adminNames,
        personalNames,
        teamNames,
        teamWs: teamWsResolved,
        selection: resolveLearningContentTemplateSelection(
          templateSelectionRef.current,
          adminNames,
          personalNames,
          teamNames
        ),
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [applyTemplateBootstrap, effectiveFocusGradeId, gradeNumber, workspaceId, subCategory]);

  const handleCreateTemplate = async () => {
    const name = newTemplateName.trim();
    const targets = addTemplateScopeToTargets(addTemplateScope);
    if (!workspaceId || !effectiveFocusGradeId || !name || targets.length === 0) return;
    if (personalTemplates.includes(name)) return;
    setTemplateBusy(true);
    try {
      for (const sc of targets) {
        await createWorkspaceLearningContentTemplate({
          workspaceId,
          gradeId: effectiveFocusGradeId,
          gradeNumber,
          subCategory: sc,
          templateName: name,
        });
      }
      const primary = pickPrimarySubCategory(targets);
      const teamWs =
        teamLeaderLearningWorkspaceId ??
        (await getTeamLeaderWorkspaceIdForLearningShare(workspaceId).catch(() => null));
      const teamShare =
        teamShareGradeId ??
        (teamWs ? await getWorkspaceShareGradeId(teamWs).catch(() => null) : null);
      if (teamShare) setTeamShareGradeId(teamShare);
      const [adminList, wsList, teamList] = await Promise.all([
        getAdminLearningContentTemplates(workspaceId, gradeNumber, primary).catch(() => []),
        getWorkspaceLearningContentTemplates(workspaceId, effectiveFocusGradeId, gradeNumber, primary, { forceRefresh: true }).catch(() => []),
        teamWs && teamShare
          ? getWorkspaceLearningContentTemplates(teamWs, teamShare, gradeNumber, primary, { forceRefresh: true }).catch(() => [])
          : Promise.resolve([]),
      ]);
      const adminNames = (adminList ?? [])
        .map((row) => (row.template_name ?? '').trim())
        .filter(Boolean);
      const personalNames = (wsList ?? [])
        .map((row) => (row.template_name ?? '').trim())
        .filter(Boolean);
      const teamNames = (teamList ?? [])
        .map((row) => (row.template_name ?? '').trim())
        .filter(Boolean);
      setAdminTemplateNames(adminNames);
      setPersonalTemplates(personalNames);
      if (teamWs) setTeamLeaderLearningWorkspaceId(teamWs);
      setTeamTemplateNames(teamNames);
      setSubCategory(primary);
      setTemplateSelection({ scope: 'user', name });
      setTemplateOrderDirty(false);
      setTemplateOriginalNameByCurrent(Object.fromEntries(personalNames.map((template) => [template, template])));
      setTemplateModalOpen(false);
      setNewTemplateName('');
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleTemplateDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    const fromName = String(active.id ?? '');
    const toName = String(over.id ?? '');
    if (!fromName || !toName || fromName === toName) return;
    const from = personalTemplates.indexOf(fromName);
    const to = personalTemplates.indexOf(toName);
    if (from < 0 || to < 0) return;
    const next = arrayMove(personalTemplates, from, to);
    setPersonalTemplates(next);
    setTemplateOrderDirty(true);
  };

  const openRenameModal = (name: string) => {
    setRenameSourceName(name);
    setRenameDraftName(name);
    setRenameModalOpen(true);
  };

  const openDeleteModal = (name: string) => {
    setDeleteTargetName(name);
    setDeleteModalOpen(true);
  };

  const handleRenameTemplate = async () => {
    const nextName = renameDraftName.trim();
    const fromName = renameSourceName.trim();
    if (!workspaceId || !effectiveFocusGradeId || !fromName || !nextName || nextName === fromName) {
      setRenameModalOpen(false);
      return;
    }
    if (personalTemplates.some((p) => p !== fromName && p === nextName)) {
      return;
    }
    setTemplateBusy(true);
    try {
      await renameWorkspaceLearningContentTemplate({
        workspaceId,
        gradeId: effectiveFocusGradeId,
        gradeNumber,
        subCategory,
        fromTemplateName: fromName,
        toTemplateName: nextName,
      });
      const nextTemplates = personalTemplates.map((name) => (name === fromName ? nextName : name));
      setPersonalTemplates(nextTemplates);
      setTemplateOriginalNameByCurrent((prev) => {
        const next = { ...prev };
        delete next[fromName];
        next[nextName] = nextName;
        return next;
      });
      if (templateScope === 'user' && templateName === fromName) {
        setTemplateSelection({ scope: 'user', name: nextName });
      }
      setRenameModalOpen(false);
    } catch (e) {
      showSaveError(e instanceof Error ? e.message : '편제 이름 변경에 실패했습니다.');
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleDeleteTemplate = async () => {
    const name = deleteTargetName.trim();
    if (!workspaceId || !effectiveFocusGradeId || !name) return;
    setTemplateBusy(true);
    try {
      await deleteWorkspaceLearningContentTemplate({
        workspaceId,
        gradeId: effectiveFocusGradeId,
        gradeNumber,
        subCategory,
        templateName: name,
      });
      const next = personalTemplates.filter((v) => v !== name);
      setPersonalTemplates(next);
      setTemplateOriginalNameByCurrent((prev) => {
        const out = { ...prev };
        delete out[name];
        return out;
      });
      if (templateScope === 'user' && templateName === name) {
        const nextName = next[0] ?? adminTemplateNames[0] ?? teamTemplateNames[0] ?? '';
        const nextScope = next[0] ? 'user' : adminTemplateNames[0] ? 'admin' : teamTemplateNames[0] ? 'team' : 'admin';
        setTemplateSelection({ scope: nextScope, name: nextName });
      }
      setDeleteModalOpen(false);
      setDeleteTargetName('');
      setTemplateOrderDirty(false);
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleSaveAll = async () => {
    if (!workspaceId || !effectiveFocusGradeId) return;
    const pendingRenames = Object.entries(templateOriginalNameByCurrent)
      .filter(([currentName, originalName]) => !!currentName && !!originalName && currentName !== originalName)
      .map(([currentName, originalName]) => ({ fromName: originalName, toName: currentName }));
    if (!tableActionState.canSave && !templateOrderDirty && pendingRenames.length === 0) return;
    setTemplateBusy(true);
    try {
      for (const rename of pendingRenames) {
        await renameWorkspaceLearningContentTemplate({
          workspaceId,
          gradeId: effectiveFocusGradeId,
          gradeNumber,
          subCategory,
          fromTemplateName: rename.fromName,
          toTemplateName: rename.toName,
        });
      }
      if (templateOrderDirty) {
        await reorderWorkspaceLearningContentTemplates({
          workspaceId,
          gradeId: effectiveFocusGradeId,
          gradeNumber,
          subCategory,
          orderedTemplateNames: personalTemplates,
        });
        setTemplateOrderDirty(false);
      }
      if (tableActionState.canSave) {
        await tableActions?.save();
      }
      setTemplateOriginalNameByCurrent(
        Object.fromEntries(personalTemplates.map((name) => [name, name]))
      );
      showSaveSuccess(`${subCategoryLabel} 데이터가 저장되었습니다.`);
    } finally {
      setTemplateBusy(false);
    }
  };

  const hasPendingRename = Object.entries(templateOriginalNameByCurrent).some(
    ([currentName, originalName]) => currentName !== originalName
  );
  const isPageDirty = tableActionState.canSave || templateOrderDirty || hasPendingRename;

  React.useEffect(() => {
    setWorkspaceLearningContentDirty(isPageDirty);
    return () => setWorkspaceLearningContentDirty(false);
  }, [isPageDirty, setWorkspaceLearningContentDirty]);

  React.useEffect(() => {
    saveWorkspaceLearningContentRef.current = { save: handleSaveAll };
    return () => {
      saveWorkspaceLearningContentRef.current = null;
    };
  }, [handleSaveAll, saveWorkspaceLearningContentRef]);

  return (
    <section
      className="cp-admin-learning-page cp-admin-learning-page--viewport-fill"
      data-tutorial-lc="page"
    >
      <div className="cp-page-title-row cp-admin-notices-page-title-row">
        <div>
          <h2 data-tutorial-lc="title">기본 학습내용 등록</h2>
        </div>
        <div className="cp-workspace-learning-title-actions cp-page-save-actions--chunky" role="group" aria-label="편집 저장 도구">
          <ProgramToolbarUndoRedoButtons
            canUndo={tableActionState.canUndo}
            canRedo={tableActionState.canRedo}
            onUndo={() => tableActions?.undo()}
            onRedo={() => tableActions?.redo()}
            disabled={!tableActions}
          />
          <button
            type="button"
            className="cp-btn-save-all"
            data-tutorial-lc="save"
            onClick={() => void handleSaveAll()}
            disabled={templateBusy || saving || !isPageDirty}
            title={isPageDirty ? '저장하기' : '저장할 변경사항 없음'}
          >
            {templateBusy || saving ? '저장 중…' : isPageDirty ? '저장하기' : '변경사항 없음'}
          </button>
        </div>
      </div>

      <div className="cp-admin-learning-top-tabs-row" data-tutorial-lc="grade-term-tabs">
        <div
          className="cp-admin-learning-grade-tabs"
          role="tablist"
          aria-label="학년 카테고리"
          data-tutorial-lc="grade-tabs"
        >
          {GRADE_TABS.map((grade) => (
            <button
              key={grade}
              type="button"
              role="tab"
              aria-selected={gradeNumber === grade}
              className={`cp-admin-learning-grade-tab${gradeNumber === grade ? ' is-active' : ''}`}
              data-tutorial-lc={`grade-${grade}`}
              onClick={() => setGradeOverride(grade)}
            >
              {grade}
            </button>
          ))}
        </div>
        <div
          className="cp-admin-learning-sub-tabs"
          role="tablist"
          aria-label="하위 카테고리"
          data-tutorial-lc="term-tabs"
        >
          {SUB_CATEGORIES.map((category) => (
            <button
              key={category.key}
              type="button"
              role="tab"
              aria-selected={subCategory === category.key}
              className={`cp-admin-learning-sub-tab cp-admin-learning-sub-tab--${category.key}${
                subCategory === category.key ? ' is-active' : ''
              }`}
              data-tutorial-lc={`term-${category.key}`}
              onClick={() => setSubCategory(category.key)}
            >
              {category.label(gradeNumber)}
            </button>
          ))}
        </div>
      </div>

      <div className="cp-admin-learning-template-actions">
        <div className="cp-admin-learning-template-actions-left">
          <div
            className="cp-admin-learning-template-legend"
            role="note"
            aria-label="편제 종류 범례"
            data-tutorial-lc="legend"
          >
            <span className="cp-admin-learning-legend-item" data-tutorial-lc="legend-default">
              <span className="cp-admin-learning-template-dot cp-admin-learning-template-dot--default" aria-hidden />
              <span>기본</span>
            </span>
            <span className="cp-admin-learning-legend-item" data-tutorial-lc="legend-team">
              <span className="cp-admin-learning-template-dot cp-admin-learning-template-dot--team" aria-hidden />
              <span>팀장</span>
            </span>
            <span className="cp-admin-learning-legend-item" data-tutorial-lc="legend-user">
              <span className="cp-admin-learning-template-dot cp-admin-learning-template-dot--user" aria-hidden />
              <span>사용자</span>
            </span>
          </div>
        </div>
      </div>

      <div className="cp-admin-learning-template-block" data-tutorial-lc="template-block">
        <div className="cp-admin-learning-template-tabs-row">
          <button
            type="button"
            className="cp-btn-copy-paste cp-admin-learning-add-template-btn"
            aria-label="사용자 편제 추가하기"
            data-tutorial-lc="add-template"
            onClick={() => {
              setAddTemplateScope(defaultAddTemplateScopeForSubCategory(subCategory));
              setNewTemplateName('');
              setTemplateModalOpen(true);
            }}
          >
            <span className="cp-admin-learning-add-template-label cp-admin-learning-add-template-label--full">
              사용자 편제 추가하기 +
            </span>
            <span className="cp-admin-learning-add-template-label cp-admin-learning-add-template-label--short">사용자 편제 +</span>
          </button>
          <div
            className="cp-admin-learning-template-tabs"
            role="tablist"
            aria-label="편제 목록"
            data-tutorial-lc="template-tabs"
          >
            {adminTemplateNames.map((name) => (
              <ReadonlyAdminTemplateTabItem
                key={`admin-${name}`}
                name={name}
                active={templateScope === 'admin' && templateName === name}
                busy={templateBusy}
                onSelect={() => {
                  setTemplateSelection({ scope: 'admin', name });
                }}
              />
            ))}
            {teamReadonlyTabItems.map((item) => (
              <ReadonlyTeamLeaderTemplateTabItem
                key={item.key}
                label={item.templateName}
                active={templateScope === 'team' && templateName === item.templateName}
                busy={templateBusy}
                onSelect={() => {
                  setTemplateSelection({ scope: 'team', name: item.templateName });
                }}
              />
            ))}
            {personalTemplates.length > 0 ? (
              <DndContext sensors={templateDndSensors} collisionDetection={closestCenter} onDragEnd={handleTemplateDragEnd}>
                <SortableContext items={personalTemplates} strategy={horizontalListSortingStrategy}>
                  {personalTemplates.map((name) => (
                    <SortableTemplateItem
                      key={name}
                      id={name}
                      active={templateScope === 'user' && templateName === name}
                      busy={templateBusy}
                      onSelect={() => {
                        setTemplateSelection({ scope: 'user', name });
                      }}
                      onRename={() => openRenameModal(name)}
                      onDelete={() => openDeleteModal(name)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            ) : null}
          </div>
        </div>
        {showPublisherSubtabs ? (
          <div className="cp-admin-learning-publisher-block">
            <div className="cp-admin-learning-publisher-subtabs" role="tablist" aria-label="출판사별 보기">
              {publisherOptions.map(([key, label]) => (
                <button
                  key={key === '' ? '__pub_empty__' : key}
                  type="button"
                  role="tab"
                  aria-selected={publisherFilter === key}
                  className={`cp-admin-learning-publisher-subtab${publisherFilter === key ? ' is-active' : ''}`}
                  onClick={() => requestPublisherFilterChange(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <div className="cp-admin-learning-table-grow" data-tutorial-lc="table">
        {error ? <p className="cp-admin-learning-error">{error}</p> : null}

        {templateName ? (
          loading ? (
            <p className="cp-admin-learning-empty" role="status">
              학습내용을 불러오는 중…
            </p>
          ) : (
          <AdminLearningContentTable
            key={`${gradeNumber}-${subCategory}-${templateScope}-${templateName}`}
            workspaceId={
              isAdminTemplate
                ? null
                : teamLeaderViewActive && teamLeaderLearningWorkspaceId
                  ? teamLeaderLearningWorkspaceId
                  : workspaceId
            }
            gradeNumber={gradeNumber}
            subCategory={subCategory}
            templateName={templateName}
            toolbarTitle={`${subCategoryLabel} - ${templateName}${publisherToolbarSuffix}${userLearningToolbarSuffix}`}
            rows={rowsForTable}
            loading={loading}
            saving={saving}
            onSave={handleSave}
            showInlineActionButtons={false}
            readOnly={isAdminTemplate || teamLeaderViewActive}
            onBindActions={setTableActions}
            onActionStateChange={setTableActionState}
            slimPublisherFilterPadding={showPublisherSubtabs}
            showProgramRecordColumns
          />
          )
        ) : (
          <p className="cp-admin-learning-empty">편제를 먼저 추가해 주세요.</p>
        )}
      </div>

      {templateModalOpen ? (
        <div className="cp-modal-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && setTemplateModalOpen(false)}>
          <div
            className="cp-modal cp-admin-learning-template-modal"
            role="dialog"
            aria-modal="true"
            data-tutorial-lc="add-template-modal"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h3>편제 이름 입력</h3>
            <div className="cp-modal-body">
              <div
                className="cp-admin-learning-template-modal-scopes"
                role="group"
                aria-label="편제를 추가할 위치"
              >
                <button
                  type="button"
                  className={`cp-program-toolbar-undo-redo-btn cp-admin-learning-template-scope-pill${
                    addTemplateScope.term1 ? ' is-on' : ''
                  }`}
                  onClick={() => setAddTemplateScope((prev) => ({ common: false, term1: !prev.term1, term2: prev.term2 }))}
                >
                  1학기
                </button>
                <button
                  type="button"
                  className={`cp-program-toolbar-undo-redo-btn cp-admin-learning-template-scope-pill${
                    addTemplateScope.term2 ? ' is-on' : ''
                  }`}
                  onClick={() => setAddTemplateScope((prev) => ({ common: false, term1: prev.term1, term2: !prev.term2 }))}
                >
                  2학기
                </button>
                <button
                  type="button"
                  className={`cp-program-toolbar-undo-redo-btn cp-admin-learning-template-scope-pill${
                    addTemplateScope.common ? ' is-on' : ''
                  }`}
                  onClick={() => setAddTemplateScope({ common: true, term1: false, term2: false })}
                >
                  학기 구분 없음
                </button>
              </div>
              <input
                type="text"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                onKeyDown={(e) => {
                  const native = e.nativeEvent as KeyboardEvent;
                  if (native.isComposing || e.key !== 'Enter') return;
                  e.preventDefault();
                  createTemplateConfirmRef.current?.focus({ preventScroll: true });
                }}
                placeholder="편제 이름"
                autoFocus
                className="cp-admin-learning-template-modal-name-input"
              />
              {newTemplateNameTaken ? (
                <p className="cp-admin-learning-template-modal-hint">
                  같은 학년·학기 범위에서 이미 있는 사용자 편제 이름과는 겹칠 수 없습니다.
                </p>
              ) : null}
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setTemplateModalOpen(false)}>
                취소
              </button>
              <button
                ref={createTemplateConfirmRef}
                type="button"
                className="cp-btn-primary"
                onClick={() => void handleCreateTemplate()}
                disabled={
                  !newTemplateName.trim() ||
                  (!addTemplateScope.common && !addTemplateScope.term1 && !addTemplateScope.term2) ||
                  templateBusy ||
                  newTemplateNameTaken
                }
              >
                확인
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {renameModalOpen ? (
        <div className="cp-modal-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && setRenameModalOpen(false)}>
          <div className="cp-modal cp-admin-learning-template-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <h3>편제 이름 수정</h3>
            <div className="cp-modal-body">
              <input
                type="text"
                value={renameDraftName}
                onChange={(e) => setRenameDraftName(e.target.value)}
                placeholder="새 편제 이름"
                autoFocus
              />
              {renameTargetConflicts ? (
                <p className="cp-admin-learning-template-modal-hint">다른 사용자 편제 이름과 겹칩니다.</p>
              ) : null}
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setRenameModalOpen(false)}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => void handleRenameTemplate()}
                disabled={!renameDraftName.trim() || templateBusy || renameTargetConflicts}
              >
                수정
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {deleteModalOpen ? (
        <div className="cp-modal-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && setDeleteModalOpen(false)}>
          <div className="cp-modal cp-admin-learning-template-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <h3>편제 삭제</h3>
            <div className="cp-modal-body">
              <p>
                `{deleteTargetName}` 편제를 삭제하면 해당 편제의 학습내용 데이터도 모두 삭제됩니다.
                <br />
                계속하시겠습니까?
              </p>
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setDeleteModalOpen(false)}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => void handleDeleteTemplate()}
                disabled={templateBusy}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <button
        type="button"
        className="cp-fab-save"
        onClick={() => void handleSaveAll()}
        disabled={templateBusy || saving || !isPageDirty}
        aria-label="저장하기"
        title={isPageDirty ? '저장하기' : '저장할 변경사항 없음'}
      >
        <img
          src={`${process.env.PUBLIC_URL || ''}/Save.svg`}
          alt=""
          className="cp-fab-save-icon"
        />
      </button>
      <AutoStatusToast state={saveToastState} />
    </section>
  );
};

export default WorkspaceLearningContentPage;
